import { useState, useEffect } from 'react'

/**
 * Decide whether a logo needs a `brightness(0) invert(1)` filter to stay
 * legible against chrome of the opposite mode.
 *
 * A logo with a transparent background is a bare wordmark or glyph, drawn in
 * whatever colour the source file happens to use, so it has to be inverted when
 * the surface behind it flips. A logo that carries its own opaque background
 * must never be filtered: inverting one flattens it into a silhouette.
 *
 * ## Why the answer comes from the server
 *
 * Saved brand logos are served from `storage.googleapis.com`, which sends no
 * `access-control-allow-origin` for these buckets. That makes a client-side
 * canvas reading impossible, in both directions:
 *
 *   - with `crossOrigin = 'anonymous'`, the probe image fails to load at all
 *     and `onerror` fires
 *   - without it, the image loads but taints the canvas, and `getImageData`
 *     throws `SecurityError`
 *
 * This hook used to take the first path and so returned its fallback for every
 * saved logo, in every org, every time. The measurement now happens on upload,
 * where the bytes are actually readable, and arrives here as `serverFlag` (see
 * `apps/backend/src/users/utils/brand-logo-inversion.ts`).
 *
 * ## Why a local probe survives
 *
 * A logo staged in the file picker but not yet saved has a `blob:` or `data:`
 * URL that this page may read, and no server measurement yet. That is the one
 * case where the canvas probe works, so it stays, and it takes precedence: it
 * is measuring the bytes the user is looking at, while `serverFlag` still
 * describes the previously saved logo.
 *
 * ## Resolution order
 *
 * 1. `src` is locally readable (`blob:`, `data:`, same-origin) — probe it.
 * 2. Otherwise use `serverFlag` when it is a boolean.
 * 3. Otherwise return `true`. Unknown means invert, which is the safe default
 *    for the bare wordmarks that make up most uploads. `null`/`undefined` must
 *    never collapse to `false`, which asserts the logo has its own background.
 *
 * @param src        The logo URL being rendered, or null when there is none.
 * @param serverFlag The persisted measurement for that logo. `null` or
 *                   `undefined` means not measured.
 */
export function useLogoNeedsInversion(
  src: string | null | undefined,
  serverFlag?: boolean | null,
): boolean {
  const [needsInversion, setNeedsInversion] = useState(true)

  useEffect(() => {
    if (!src) {
      setNeedsInversion(true)
      return
    }

    if (!isCanvasReadable(src)) {
      setNeedsInversion(typeof serverFlag === 'boolean' ? serverFlag : true)
      return
    }

    let cancelled = false

    const img = new Image()

    img.onload = () => {
      if (cancelled) return
      try {
        const canvas = document.createElement('canvas')
        const size = Math.min(img.naturalWidth, img.naturalHeight, 64)
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          setNeedsInversion(true)
          return
        }

        ctx.drawImage(img, 0, 0, size, size)
        const { data } = ctx.getImageData(0, 0, size, size)

        let opaquePixels = 0
        const totalPixels = data.length / 4

        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] >= 128) opaquePixels++
        }

        const isTransparent = opaquePixels < totalPixels * 0.5
        setNeedsInversion(isTransparent)
      } catch {
        // `isCanvasReadable` already excluded the tainting sources, so reaching
        // here means the canvas is unavailable rather than cross-origin.
        setNeedsInversion(true)
      }
    }

    img.onerror = () => {
      if (!cancelled) setNeedsInversion(true)
    }

    img.src = src

    return () => {
      cancelled = true
    }
  }, [src, serverFlag])

  return needsInversion
}

/**
 * Whether this page may read the pixels of `src` back out of a canvas.
 *
 * `blob:` and `data:` URLs carry the page's own origin, and a same-origin URL
 * needs no CORS grant. Everything else taints the canvas unless the host opts
 * in with `access-control-allow-origin`, which the brand images bucket does
 * not.
 */
function isCanvasReadable(src: string): boolean {
  if (src.startsWith('data:')) return true

  try {
    const url = new URL(src, window.location.href)
    if (url.protocol === 'blob:') return true
    return url.origin === window.location.origin
  } catch {
    return false
  }
}
